import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import SupportModal from '../order/SupportModal'
import './FAQ.css'

interface FAQItem {
    question: string
    answer: string
}

export default function FAQ() {
    const { t } = useTranslation()
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const [showSupport, setShowSupport] = useState(false)

    const faqs: FAQItem[] = [
        {
            question: t('faq.q1'),
            answer: t('faq.a1')
        },
        {
            question: t('faq.q2'),
            answer: t('faq.a2')
        },
        {
            question: t('faq.q3'),
            answer: t('faq.a3')
        },
        {
            question: t('faq.q4'),
            answer: t('faq.a4')
        },
        {
            question: t('faq.q5'),
            answer: t('faq.a5')
        }
    ]

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index)
    }

    return (
        <section className="faq-section" id="faq">
            <div className="container">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="section-title">
                        {t('faq.title')} <span className="text-gradient">{t('faq.titleHighlight')}</span>
                    </h2>
                    <p className="section-subtitle">
                        {t('faq.subtitle')}
                    </p>
                </motion.div>

                <div className="faq-container">
                    {faqs.map((faq, index) => (
                        <motion.div
                            key={index}
                            className="faq-item"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.05 }}
                        >
                            <button
                                className={`faq-question ${openIndex === index ? 'active' : ''}`}
                                onClick={() => toggleFAQ(index)}
                            >
                                <span>{faq.question}</span>
                                <span className="faq-icon">{openIndex === index ? '−' : '+'}</span>
                            </button>

                            <AnimatePresence>
                                {openIndex === index && (
                                    <motion.div
                                        className="faq-answer"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <p>{faq.answer}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="faq-divider"></div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    className="faq-support-cta"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                >
                    <div className="support-box glass">
                        <div className="support-icon">💬</div>
                        <div className="support-content">
                            <h3>{t('faq.supportTitle')}</h3>
                            <p>{t('faq.supportDesc')}</p>
                        </div>
                        <button className="btn btn-primary" onClick={() => setShowSupport(true)}>
                            {t('common.contactSupport')}
                        </button>
                    </div>
                </motion.div>
            </div>

            <SupportModal isOpen={showSupport} onClose={() => setShowSupport(false)} />
        </section>
    )
}
